<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { isWebGL2Supported } from '@/gl/context'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { useLayout } from '@/composables/useLayout'
import { useShortcuts } from '@/composables/useShortcuts'
import { useUnsavedGuard } from '@/composables/useUnsavedGuard'
import { imageFromDataTransfer } from '@/io/clipboard'
import { pasteAsLayer } from '@/app/actions'
import { onBeforeUnmount } from 'vue'
import MenuBar from '@/components/layout/MenuBar.vue'
import OptionsBar from '@/components/layout/OptionsBar.vue'
import ToolBar from '@/components/layout/ToolBar.vue'
import CanvasView from '@/components/layout/CanvasView.vue'
import DockRight from '@/components/layout/DockRight.vue'
import StatusBar from '@/components/layout/StatusBar.vue'
import Toast from '@/components/common/Toast.vue'
import UnsupportedScreen from '@/components/common/UnsupportedScreen.vue'
import MobileNotice from '@/components/common/MobileNotice.vue'
import ToolGuidePopover from '@/components/common/ToolGuidePopover.vue'
import { useToolHints } from '@/composables/useToolHints'
import NewDocumentDialog from '@/components/dialogs/NewDocumentDialog.vue'
import ExportDialog from '@/components/dialogs/ExportDialog.vue'
import AboutDialog from '@/components/dialogs/AboutDialog.vue'
import ShortcutsDialog from '@/components/dialogs/ShortcutsDialog.vue'
import SelectionModifyDialog from '@/components/dialogs/SelectionModifyDialog.vue'
import CanvasSizeDialog from '@/components/dialogs/CanvasSizeDialog.vue'
import ImageSizeDialog from '@/components/dialogs/ImageSizeDialog.vue'
import LayerStyleDialog from '@/components/dialogs/LayerStyleDialog.vue'
import FilterDialog from '@/components/dialogs/FilterDialog.vue'
import LiquifyDialog from '@/components/dialogs/LiquifyDialog.vue'
import ColorRangeDialog from '@/components/dialogs/ColorRangeDialog.vue'
import RefineEdgeDialog from '@/components/dialogs/RefineEdgeDialog.vue'
import AiConsentDialog from '@/components/dialogs/AiConsentDialog.vue'
import FillDialog from '@/components/dialogs/FillDialog.vue'
import StrokeDialog from '@/components/dialogs/StrokeDialog.vue'
import RotateArbitraryDialog from '@/components/dialogs/RotateArbitraryDialog.vue'
import FontEmbedDialog from '@/components/dialogs/FontEmbedDialog.vue'

const supported = ref(true)
// スマートフォン (粗いポインタ + ホバーなし + 短辺が狭い) は対象外にする。
// タッチ対応のノート PC はマウスがあるので hover: hover になり、ここには入らない。
const isPhone = ref(false)
const phoneBypass = ref(false)
function checkPhone() {
  if (typeof window === 'undefined' || !window.matchMedia) return
  const coarse = window.matchMedia('(pointer: coarse)').matches
  const noHover = window.matchMedia('(hover: none)').matches
  const short = Math.min(window.innerWidth, window.innerHeight)
  isPhone.value = coarse && noHover && short < 700
}
async function onPaste(e: ClipboardEvent) {
  if (e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return
  const img = await imageFromDataTransfer(e.clipboardData)
  if (img) {
    e.preventDefault()
    await pasteAsLayer(img)
  }
}
onMounted(() => {
  checkPhone()
  window.addEventListener('resize', checkPhone)
  supported.value = isWebGL2Supported()
  document.addEventListener('paste', onPaste)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', checkPhone)
  document.removeEventListener('paste', onPaste)
})

const docStore = useDocumentStore()
const ui = useUiStore()
const layout = useLayout()
useShortcuts()
useUnsavedGuard()
useToolHints()

const gridCols = computed(() => `44px 1fr 4px ${layout.rightWidth.value}px`)

function startResize(e: PointerEvent) {
  const startX = e.clientX
  const startW = layout.rightWidth.value
  const prevCursor = document.body.style.cursor
  document.body.style.cursor = 'col-resize'
  const move = (ev: PointerEvent) => layout.setRightWidth(startW - (ev.clientX - startX))
  const up = () => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    document.body.style.cursor = prevCursor
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}
</script>

<template>
  <MobileNotice v-if="isPhone && !phoneBypass" @continue="phoneBypass = true" />
  <UnsupportedScreen v-else-if="!supported" />
  <div v-else class="app">
    <MenuBar />
    <OptionsBar />
    <div class="main" :style="{ gridTemplateColumns: gridCols }">
      <ToolBar />
      <CanvasView :key="docStore.sessionVersion" />
      <div class="resizer" @pointerdown="startResize" @dblclick="layout.reset('rightWidth')" />
      <DockRight />
    </div>
    <StatusBar />
    <Toast />
    <ToolGuidePopover v-if="ui.toolGuideOpen" @close="ui.toolGuideOpen = false" />
    <NewDocumentDialog v-if="ui.dialog === 'new'" @close="ui.dialog = null" />
    <ExportDialog v-if="ui.dialog === 'export'" @close="ui.dialog = null" />
    <AboutDialog v-if="ui.dialog === 'about'" @close="ui.dialog = null" />
    <ShortcutsDialog v-if="ui.dialog === 'shortcuts'" @close="ui.dialog = null" />
    <SelectionModifyDialog v-if="ui.dialog === 'selectionModify'" :kind="(ui.dialogArg as any) ?? 'feather'" @close="ui.dialog = null" />
    <CanvasSizeDialog v-if="ui.dialog === 'canvasSize'" @close="ui.dialog = null" />
    <ImageSizeDialog v-if="ui.dialog === 'imageSize'" @close="ui.dialog = null" />
    <LayerStyleDialog v-if="ui.dialog === 'layerStyle'" @close="ui.dialog = null" />
    <FilterDialog v-if="ui.dialog === 'filter'" :filter-id="ui.dialogArg ?? ''" @close="ui.dialog = null" />
    <LiquifyDialog v-if="ui.dialog === 'liquify'" @close="ui.dialog = null" />
    <ColorRangeDialog v-if="ui.dialog === 'colorRange'" @close="ui.dialog = null" />
    <RefineEdgeDialog v-if="ui.dialog === 'refineEdge'" @close="ui.dialog = null" />
    <AiConsentDialog v-if="ui.dialog === 'aiConsent'" @close="ui.dialog = null" />
    <FillDialog v-if="ui.dialog === 'fill'" @close="ui.dialog = null" />
    <StrokeDialog v-if="ui.dialog === 'stroke'" @close="ui.dialog = null" />
    <RotateArbitraryDialog v-if="ui.dialog === 'rotateArbitrary'" @close="ui.dialog = null" />
    <FontEmbedDialog v-if="ui.dialog === 'fontEmbed'" @close="ui.dialog = null" />
  </div>
</template>

<style scoped>
.app {
  height: 100%;
  display: grid;
  grid-template-rows: 28px 34px 1fr 22px;
  background: var(--bg-0);
}
.main {
  display: grid;
  min-height: 0;
  border-top: 1px solid var(--line-region);
  border-bottom: 1px solid var(--line-region);
}
.resizer {
  cursor: col-resize;
  background: var(--line-region);
}
.resizer:hover {
  background: var(--accent);
}
</style>
